import type { User } from "@supabase/supabase-js";
import type { PostHog } from "posthog-js";

export const identifyPostHogUser = (posthog: PostHog, user: User) => {
  posthog.identify(user.id, {
    email: user.email,
    version: "v3",
    // Person properties used by the pricing-limit experiment flag's
    // release conditions (signed_up_at is_date_after launch, is_guest
    // is_not true). Guests are API-managed ghost accounts that never log
    // in through the web UI, so is_guest is always false here.
    signed_up_at: user.created_at,
    is_guest: "false",
  });
};

// Flags reload after identify() can take a beat; don't hold the submit form
// hostage to a slow or blocked /flags call.
const FLAGS_RELOAD_TIMEOUT_MS = 1500;

// Identifies the user, then resolves once feature flags have reloaded under
// their id (or after the timeout). For a visitor who just signed in on a
// marketing page: the submit form evaluates experiment flags (reframe focus,
// pricing limit, source range) that klap-api buckets by profile id, so the
// form must not open on the anonymous visitor's arms.
export const identifyAndReloadFlags = (
  posthog: PostHog | undefined,
  user: User,
): Promise<void> =>
  new Promise((resolve) => {
    if (!posthog) return resolve();

    let subscribed = false;
    let unsubscribe: () => void = () => {};
    const done = () => {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    };
    const timer = setTimeout(done, FLAGS_RELOAD_TIMEOUT_MS);

    // onFeatureFlags calls back synchronously with the flags already loaded
    // (the anonymous visitor's): skip that call, wait for the reload.
    unsubscribe = posthog.onFeatureFlags(() => {
      if (subscribed) done();
    });
    subscribed = true;

    identifyPostHogUser(posthog, user);
    // identify() only reloads flags when the distinct id changes. If this user
    // was already identified (e.g. by usePostHogIdentify a render earlier),
    // force a reload so there is a fresh response to wait for; while one is
    // already queued this is a no-op.
    posthog.reloadFeatureFlags();
  });

// Resolves once feature flags have loaded (straight away if they already
// have), or after the timeout: a visitor who pastes a link the moment the page
// loads must not be bucketed before /flags has answered.
export const waitForFeatureFlags = (
  posthog: PostHog | undefined,
  timeoutMs = FLAGS_RELOAD_TIMEOUT_MS,
): Promise<void> =>
  new Promise((resolve) => {
    if (!posthog) return resolve();

    let settled = false;
    let unsubscribe: (() => void) | undefined;
    const done = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsubscribe?.();
      resolve();
    };
    const timer = setTimeout(done, timeoutMs);
    // Calls back synchronously when the flags are already loaded.
    unsubscribe = posthog.onFeatureFlags(done);
    if (settled) unsubscribe();
  });

// Google and the password form also sign existing users in. A new account
// was confirmed within the window: email signups confirm when the code or
// link is used, Google ones on creation.
export const isNewlyConfirmedUser = (
  user: Pick<User, "email_confirmed_at" | "created_at">,
  now = Date.now(),
  windowMs = 30 * 60 * 1000,
): boolean =>
  now - new Date(user.email_confirmed_at ?? user.created_at).getTime() <
  windowMs;

// Wide enough for a link confirmed in one browser (where the sign-in can't
// complete) and signed into from another later on.
const SIGNED_UP_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const SIGNED_UP_TRACKED_KEY = "klap_signed_up_tracked";

// "Signed Up", once per new account in this browser, wherever the signup
// happened: the step both arms of the signup-modal-flow experiment share
// (helpers/signupFlowExperiment.ts). Count unique persons: a signup that
// spans two browsers fires in each. Call after identify so it lands on the
// user's person.
export const captureSignedUpOnce = (posthog: PostHog, user: User) => {
  // API-created guests (embeds) never sign up themselves.
  if (user.user_metadata?.ghost_user) return;
  if (!isNewlyConfirmedUser(user, Date.now(), SIGNED_UP_WINDOW_MS)) return;
  try {
    if (localStorage.getItem(SIGNED_UP_TRACKED_KEY) === user.id) return;
    localStorage.setItem(SIGNED_UP_TRACKED_KEY, user.id);
  } catch {
    // No storage (private mode): may repeat within the window, still counts
    // once per person in PostHog.
  }
  posthog.capture("Signed Up", {
    provider: user.app_metadata?.provider,
    signup_flow: user.user_metadata?.signup_flow ?? null,
  });
};

// The visitor's anonymous PostHog id, saved on the account at signup
// (`user_metadata.ph_anon_id`, see AuthUI) while they're still anonymous.
export const anonymousIdForSignup = (
  posthog: PostHog | undefined,
): string | undefined =>
  posthog && posthog.get_property("$user_state") !== "identified"
    ? posthog.get_distinct_id()
    : undefined;

const ALIASED_KEY = "klap_ph_signup_aliased";

// Merges the anonymous person the visitor signed up as into the user, from
// whichever browser they first sign in on. Without it, an email confirmed
// in another browser (a phone's mail app) leaves the signup browser's
// events, the signup-modal-flow exposure included, on an orphan person, and
// the user's conversions never join them. Where identify() already ran in
// the signup browser this is a no-op merge. Call after identify.
export const aliasSignupAnonymousId = (posthog: PostHog, user: User) => {
  const anonymousId = user.user_metadata?.ph_anon_id;
  if (typeof anonymousId !== "string" || !anonymousId) return;
  if (anonymousId === user.id || anonymousId === posthog.get_distinct_id()) {
    return;
  }
  try {
    if (localStorage.getItem(ALIASED_KEY) === user.id) return;
    localStorage.setItem(ALIASED_KEY, user.id);
  } catch {
    // No storage: repeats are no-op merges.
  }
  posthog.alias(anonymousId);
};
